import type { TableItem } from '#/domains/landing/types/content';
import InlineLinks from './InlineLinks';

interface ContentTableProps {
  table: TableItem;
}

/**
 * Mystique 3.0 리스트 관용구를 따른다 — 테두리 대신 `surface-basic` 채움 카드,
 * `mds-radius-md`(18px), 행 구분은 `divider-basic-high` 선 하나.
 * 줄무늬(zebra)·중첩 카드·셀 세로선은 쓰지 않는다 (디자인시스템에 없는 장치다).
 *
 * **표는 모든 화면 폭에서 표 그대로 유지한다.** 좁은 화면에서 행을 카드로 펼치면
 * 열 사이의 비교(이 표의 존재 이유)가 사라진다 — 대신 열이 3개 이상이면 최소 너비를
 * 주어 가로 스크롤로 넘긴다.
 * (이 서비스는 Tailwind v3라 `@3o3/mystique-zero`를 직접 쓰지 않고 토큰 값만 맞춘다.
 *  gray-5=surface-basic · gray-20=divider-basic-high · gray-60=text-body-basic ·
 *  gray-80=text-body-high · gray-90=text-title-basic)
 */

type RenderedCell = {
  text: string;
  colIndex: number;
  rowSpan?: number;
};

function getFirstColumnLabel(rows: string[][], rowIndex: number): string {
  for (let i = rowIndex; i >= 0; i -= 1) {
    const label = rows[i][0]?.trim();
    if (label) return label;
  }
  return '';
}

function buildRenderedRows(
  rows: string[][],
  groupFirstColumn: boolean
): RenderedCell[][] {
  if (!groupFirstColumn) {
    return rows.map(row => row.map((text, colIndex) => ({ text, colIndex })));
  }

  return rows.map((row, rowIndex) => {
    const label = getFirstColumnLabel(rows, rowIndex);
    const prevLabel =
      rowIndex > 0 ? getFirstColumnLabel(rows, rowIndex - 1) : '';
    const isContinuation = rowIndex > 0 && label === prevLabel;

    const cells: RenderedCell[] = [];

    if (!isContinuation) {
      let rowSpan = 1;
      for (let i = rowIndex + 1; i < rows.length; i += 1) {
        if (getFirstColumnLabel(rows, i) === label) rowSpan += 1;
        else break;
      }
      cells.push({
        text: label,
        colIndex: 0,
        rowSpan: rowSpan > 1 ? rowSpan : undefined,
      });
    }

    for (let j = 1; j < row.length; j += 1) {
      cells.push({ text: row[j], colIndex: j });
    }

    return cells;
  });
}

export default function ContentTable({ table }: ContentTableProps) {
  const isKeyValueTwoCol = table.headers.length === 2;
  const groupFirstColumn = table.groupFirstColumn === true;
  const renderedRows = buildRenderedRows(table.rows, groupFirstColumn);
  // 2·3열(전체 표의 87%)은 좁은 셀 패딩으로 360px 화면에도 들어간다 — 스크롤 없이 표 그대로.
  // 4열 이상은 우겨넣으면 글자가 한두 자씩 끊기므로 최소 너비를 주고 가로로 넘긴다
  // (컨테이너가 overflow-x-auto).
  const minWidthClass =
    table.headers.length >= 4 ? 'min-w-[560px] md:min-w-0' : '';

  return (
    <div className="w-full overflow-hidden rounded-[18px] bg-gray-5">
      {table.caption && (
        <div className="px-3 pb-1 pt-4 md:px-5">
          <p className="text-[16px] font-bold leading-[1.5] text-gray-90">
            {table.caption}
          </p>
        </div>
      )}
      <div className="overflow-x-auto [-webkit-overflow-scrolling:touch] [overscroll-behavior-x:contain]">
        <table
          className={`w-full border-collapse text-[14px] leading-[1.5] ${
            isKeyValueTwoCol ? 'table-fixed' : ''
          } ${minWidthClass}`}
        >
          {isKeyValueTwoCol && (
            <colgroup>
              <col style={{ width: '30%' }} />
              <col />
            </colgroup>
          )}
          {groupFirstColumn && !isKeyValueTwoCol && (
            <colgroup>
              <col style={{ width: '22%' }} />
              <col style={{ width: '28%' }} />
              <col />
              <col style={{ width: '24%' }} />
            </colgroup>
          )}
          {!isKeyValueTwoCol &&
            !groupFirstColumn &&
            table.headers.length === 3 && (
              <colgroup>
                <col style={{ width: '26%' }} />
                <col style={{ width: '38%' }} />
                <col />
              </colgroup>
            )}
          {!isKeyValueTwoCol &&
            !groupFirstColumn &&
            table.headers.length >= 4 && (
              // Only the label column is pinned; the data columns are sized by
              // their own content so a long value doesn't wrap next to a roomy
              // neighbor.
              <colgroup>
                <col style={{ width: '22%' }} />
              </colgroup>
            )}
          <thead>
            <tr>
              {table.headers.map((header, i) => (
                <th
                  key={i}
                  className={`border-b border-gray-20 px-3 py-3 text-left text-[14px] font-bold text-gray-80 md:px-5 ${
                    isKeyValueTwoCol ? 'align-top' : ''
                  }`}
                >
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {renderedRows.map((cells, i) => (
              <tr key={i}>
                {cells.map(cell => (
                  <td
                    key={cell.colIndex}
                    rowSpan={cell.rowSpan}
                    className={`border-t border-gray-20 px-3 py-3 text-left md:px-5 ${
                      isKeyValueTwoCol ? 'align-top' : ''
                    } ${cell.rowSpan ? 'align-middle' : ''} ${
                      cell.colIndex === 0
                        ? 'font-medium text-gray-90'
                        : 'font-normal text-gray-60'
                    }`}
                  >
                    <InlineLinks text={cell.text} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
